import { useState } from 'react';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import RelatedServices from '../../../components/feature/RelatedServices';
import RelatedIndustries from '../../../components/feature/RelatedIndustries';

export default function WellnessCentersPage() {
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    practiceName: '',
    message: ''
  });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await fetch('https://readdy.ai/api/form/d62jacgr3m6fkobkntvg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as any).toString()
      });
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', practiceName: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar scrolled={scrolled} />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-br from-[#F5F3FF] to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-[#7C3AED]/10 rounded-full mb-6">
                <span className="text-[#7C3AED] font-semibold text-sm">Wellness Center Marketing</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Grow Your <span className="bg-gradient-to-r from-[#7C3AED] to-[#A855F7] bg-clip-text text-transparent">Wellness Center</span> with Consistent Patient Flow
              </h1>
              <p className="text-xl text-gray-600 mb-10 leading-relaxed">
                HIPAA-compliant digital marketing and automated patient acquisition for women's, men's, and family wellness centers seeking health-conscious patients committed to comprehensive care.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#contact-form" className="px-8 py-4 bg-gradient-to-r from-[#7C3AED] to-[#A855F7] text-white font-semibold rounded-lg hover:shadow-xl transition-all whitespace-nowrap">
                  Get Your Growth Plan
                </a>
                <a href="#solutions" className="px-8 py-4 bg-white text-[#7C3AED] font-semibold rounded-lg border-2 border-[#7C3AED] hover:bg-[#7C3AED]/5 transition-all whitespace-nowrap">
                  See How We Help
                </a>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://public.readdy.ai/ai/img_res/67b10ed998be1887d01ab8ee7d00c16a.jpg"
                alt="Wellness Center"
                className="rounded-2xl shadow-2xl w-full h-auto object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Challenges Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Marketing Challenges for Wellness Centers
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We understand the unique obstacles wellness center owners face
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-user-search-line',
                title: 'Attracting Ideal Wellness Patients',
                description: 'Finding patients who value holistic, preventive care and are willing to invest in their health'
              },
              {
                icon: 'ri-service-line',
                title: 'Promoting Multiple Services',
                description: 'Marketing diverse wellness offerings from hormone therapy to IV nutrition to aesthetic treatments'
              },
              {
                icon: 'ri-money-dollar-circle-line',
                title: 'Converting Cash-Pay Patients',
                description: 'Attracting patients willing to pay out-of-pocket for comprehensive wellness services'
              },
              {
                icon: 'ri-team-line',
                title: 'Standing Out in Crowded Markets',
                description: 'Differentiating your wellness center from medical spas, gyms, and traditional medical practices'
              },
              {
                icon: 'ri-shield-cross-line',
                title: 'HIPAA-Compliant Marketing',
                description: 'Running effective digital campaigns while protecting patient privacy and health information'
              },
              {
                icon: 'ri-calendar-line',
                title: 'Maintaining Consistent Bookings',
                description: 'Keeping your schedule full across multiple providers and service lines'
              }
            ].map((challenge, index) => (
              <div key={index} className="p-8 bg-gray-50 rounded-xl hover:shadow-lg transition-all">
                <div className="w-14 h-14 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-lg flex items-center justify-center mb-6">
                  <i className={`${challenge.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{challenge.title}</h3>
                <p className="text-gray-600 leading-relaxed">{challenge.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section id="solutions" className="py-20 px-6 bg-gradient-to-br from-[#F5F3FF] to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Our Marketing Automation Platform Fills Your Wellness Center Automatically
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                We help wellness centers attract health-conscious patients through HIPAA-compliant digital marketing and automated systems that educate prospects about your services and convert them into loyal, long-term patients.
              </p>
              <div className="space-y-6">
                {[
                  'Automated campaigns promoting your full range of wellness services',
                  'Lead nurturing sequences for prospects researching wellness options',
                  'Patient intake and appointment booking workflows for multiple services',
                  'CRM built for multi-service wellness center operations',
                  'No missed leads - every inquiry is automatically followed up',
                  'HIPAA-conscious implementation with secure patient data handling'
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-6 h-6 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <p className="text-gray-700 font-medium">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=wellness%20center%20consultation%20room%20holistic%20health%20practitioner%20meeting%20with%20patient%20personalized%20treatment%20planning%20modern%20medical%20spa%20environment%20professional%20healthcare%20consultation%20natural%20lighting&width=600&height=700&seq=wellness-consultation&orientation=portrait"
                alt="Wellness Center Consultation"
                className="rounded-2xl shadow-2xl w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Healthcare Marketing Services for Wellness Centers
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive digital marketing solutions designed for multi-service wellness centers
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { title: 'Medical Website Design', icon: 'ri-layout-line', link: '/services/medical-website-design' },
              { title: 'Medical SEO & Local SEO', icon: 'ri-search-line', link: '/services/medical-seo' },
              { title: 'Healthcare PPC Advertising', icon: 'ri-advertisement-line', link: '/services/healthcare-ppc' },
              { title: 'Content Marketing', icon: 'ri-article-line', link: '/services/content-marketing' },
              { title: 'Marketing Automation', icon: 'ri-robot-line', link: '/services/marketing-automation' },
              { title: 'Social Media Marketing', icon: 'ri-instagram-line', link: '/services/social-media-marketing' }
            ].map((service, index) => (
              <a 
                key={index}
                href={service.link}
                className="p-8 bg-gray-50 rounded-xl hover:shadow-xl transition-all group cursor-pointer"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className={`${service.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-[#7C3AED] font-semibold flex items-center gap-2">
                  Learn More <i className="ri-arrow-right-line"></i>
                </p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-6 bg-gradient-to-br from-[#F5F3FF] to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Trusted by Leading Wellness Centers
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'Sarah Mitchell',
                title: 'Women\'s Wellness Center Owner',
                image: 'https://readdy.ai/api/search-image?query=professional%20female%20wellness%20center%20owner%20confident%20smile%20modern%20health%20facility%20business%20owner%20healthcare%20entrepreneur&width=150&height=150&seq=wellness-test-1&orientation=squarish',
                text: 'MillionDollarMed helped us attract patients who truly value comprehensive wellness care. Our bookings across all services increased 265% in 6 months with their automation platform.'
              },
              {
                name: 'Dr. James Parker',
                title: 'Family Wellness Center Medical Director',
                image: 'https://readdy.ai/api/search-image?query=professional%20male%20doctor%20family%20wellness%20medical%20director%20confident%20expression%20holistic%20health%20center%20approachable%20physician&width=150&height=150&seq=wellness-test-2&orientation=squarish',
                text: 'The automated lead nurturing system educates prospects about our services while we focus on patient care. We\'ve never had such consistent patient flow across all our offerings.'
              },
              {
                name: 'Amanda Rodriguez',
                title: 'Men\'s Health & Wellness Center Director',
                image: 'https://readdy.ai/api/search-image?query=professional%20female%20hispanic%20wellness%20director%20confident%20business%20attire%20modern%20mens%20health%20center%20healthcare%20executive&width=150&height=150&seq=wellness-test-3&orientation=squarish',
                text: 'Their HIPAA-compliant marketing strategies and automation platform transformed our center. We consistently attract ideal patients ready to invest in their health and wellness journey.'
              }
            ].map((testimonial, index) => (
              <div key={index} className="p-8 bg-white rounded-xl shadow-lg">
                <div className="flex items-center gap-4 mb-6">
                  <img 
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full object-cover object-top"
                  />
                  <div>
                    <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                    <p className="text-sm text-gray-600">{testimonial.title}</p>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed italic">"{testimonial.text}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact-form" className="py-20 px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Book Your Free Strategy Call
            </h2>
            <p className="text-xl text-gray-600">
              Let's discuss how we can help your wellness center attract more ideal patients
            </p>
          </div>
          <form onSubmit={handleSubmit} className="bg-gray-50 p-8 rounded-2xl" data-readdy-form id="wellness-centers-strategy">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#7C3AED] focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#7C3AED] focus:border-transparent text-sm"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#7C3AED] focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Practice Name *</label>
                <input
                  type="text"
                  name="practiceName"
                  value={formData.practiceName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#7C3AED] focus:border-transparent text-sm"
                />
              </div>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Tell Us About Your Wellness Center</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#7C3AED] focus:border-transparent text-sm resize-none"
                placeholder="What are your main marketing challenges?"
              ></textarea>
              <p className="text-xs text-gray-500 mt-2">Maximum 500 characters</p>
            </div>
            <button
              type="submit"
              className="w-full px-8 py-4 bg-gradient-to-r from-[#7C3AED] to-[#A855F7] text-white font-semibold rounded-lg hover:shadow-xl transition-all whitespace-nowrap"
            >
              Book My Strategy Call
            </button>
            {submitStatus === 'success' && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800 font-medium">Thank you! We'll contact you within 24 hours to schedule your strategy call.</p>
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 font-medium">Something went wrong. Please try again or email us directly.</p>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-4 text-center">
              Your information is protected and will be handled in accordance with HIPAA guidelines.
            </p>
          </form>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6 bg-gradient-to-r from-[#0D9488] to-[#14B8A6]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Grow Your Wellness Center?
          </h2>
          <p className="text-xl text-white/90 mb-10">
            Let's build a HIPAA-compliant marketing system that attracts ideal patients automatically
          </p>
          <a href="#contact-form" className="inline-block px-8 py-4 bg-white text-[#0D9488] font-semibold rounded-lg hover:shadow-2xl transition-all whitespace-nowrap cursor-pointer">
            Schedule Your Free Consultation
          </a>
        </div>
      </section>

      <RelatedServices currentSlug="wellness-centers" />
      <RelatedIndustries currentSlug="wellness-centers" />

      <Footer />
    </div>
  );
}